import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import Turnstile from "react-turnstile";
import { validarSuperposicionCambioSeccion } from "../../Api/reservation";
import { changeSectionThunk } from "../../reduxAsyncThunks/transaction.thunks";
import { useAppDispatch } from "../../app/store";
import { useToastError } from "../../hooks/useToast";
import { useVerifyTurnstile } from "../../hooks/useTurnstile";
import {
  selectFlags,
  selectTurnstileVerified,
  setConfirmChangeSectionData,
  setDesktopTab,
  setSectionToChange,
} from "../../reduxSlices/globalFlagsSlice";
import { selectDebeAlertarSuperposicion, selectStudent } from "../../reduxSlices/studentSlice";
import {
  addSectionChangeInProcess,
  addSubProcessSectionIds,
  removeSectionChangeInProcess,
  removeSubProcessSectionIds,
  selectInscribedSubjects,
  selectOfferedSubjects,
} from "../../reduxSlices/subjectsSlice";
import { getInscribedSubjectTitle, getOfferSubjectTitle } from "../../utils/changeSection";
import BannersContainer from "../BannersContainer/BannersContainer";
import DesktopSelectedSubjects from "../DesktopSelectedSubjects/DesktopSelectedSubjects";
import FlexCalendar from "../FlexCalendar/FlexCalendar";
import Modal from "../Modal/Modal";
import OfferVirtualListContainer from "../OfferVirtualListContainer/OfferVirtualListContainer";
import "./DesktopMain.css";
import Loader from "../Loader/Loader";
import InvalidatingOfferLoader from "../InvalidatingOfferLoader/InvalidatingOfferLoader";
import TransitionModal from "../TransitionModal/TransitionModal";
import { isRejected } from "@reduxjs/toolkit";
import Translatable from "../Translatable/Translatable";
import { useTranslatedText } from "../../contexts/TranslationContext";
import { useTranslatedSubjectTitle } from "../../hooks/useTranslatedSubjectTitle";

function DesktopMain() {
  const dispatch = useAppDispatch();
  const showError = useToastError();
  const { handleVerify } = useVerifyTurnstile();
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const student = useSelector(selectStudent);
  const inscripciones = useSelector(selectInscribedSubjects);
  const oferta = useSelector(selectOfferedSubjects);
  const debeAlertarSuperposicion = useSelector(selectDebeAlertarSuperposicion);
  const turnstileVerified = useSelector(selectTurnstileVerified);
  const { desktopTab, sectionToChange, confirmChangeSectionData } = useSelector(selectFlags);
  const [showModalSuperposicion, setShowModalSuperposicion] = useState<boolean>(false);
  const [showModalChangeSection, setShowModalChangeSection] = useState<boolean>(false);
  const [cursoSuperpuesto, setCursoSuperpuesto] = useState<string>("");
  const [wasChangeSectionConfirmed, setWasChangeSectionConfirmed] = useState<boolean>(false);
  const cancelLabel = useTranslatedText("Cancelar", { capitalize: true });
  const continueLabel = useTranslatedText("Continuar", { capitalize: true });
  const confirmLabel = useTranslatedText("Confirmar", { capitalize: true });
  const inscribedTitle = useTranslatedSubjectTitle(
    sectionToChange ? getInscribedSubjectTitle(sectionToChange.idMateria, inscripciones) : "",
  );
  const offerTitle = useTranslatedSubjectTitle(
    sectionToChange ? getOfferSubjectTitle(sectionToChange.idMateria, sectionToChange.id, oferta) : "",
  );
  const overlapTitle = useTranslatedSubjectTitle(cursoSuperpuesto);

  useEffect(() => {
    if (student.mostrarHorarios) {
      dispatch(setDesktopTab("calendario"));
    } else {
      dispatch(setDesktopTab("materias"));
    }
  }, [student.mostrarHorarios, dispatch]);

  useEffect(() => {
    if (debeAlertarSuperposicion && sectionToChange) {
      const validarDatos = async () => {
        if (sectionToChange && confirmChangeSectionData) {
          try {
            dispatch(
              addSectionChangeInProcess({ idCurso: sectionToChange.id, anioLectivo: sectionToChange.anioLectivo })
            );
            const { data } = await validarSuperposicionCambioSeccion(confirmChangeSectionData.reserveData);
            if (data.esValido === false) {
              setShowModalSuperposicion(true);
              setCursoSuperpuesto(data.extraData);
            } else if (data.esValido === true) {
              setShowModalChangeSection(true);
            }
          } catch (error) {
            dispatch(removeSectionChangeInProcess({ idCurso: sectionToChange.id, anioLectivo: sectionToChange.anioLectivo }));
            console.error("Error al validar superposición:", error);
          }
        }
      };
      validarDatos();
    } else if (sectionToChange) {
      dispatch(
        addSectionChangeInProcess({ idCurso: sectionToChange.id, anioLectivo: sectionToChange.anioLectivo })
      );
      setShowModalChangeSection(true);
    }
  }, [sectionToChange, confirmChangeSectionData, debeAlertarSuperposicion, dispatch]);

  const clearData = (shouldClearSectionChangeProcess = true) => {
    setShowModalSuperposicion(false);
    setShowModalChangeSection(false);
    dispatch(setSectionToChange(null));
    if (shouldClearSectionChangeProcess) {
      dispatch(
        removeSectionChangeInProcess({
          idCurso: sectionToChange?.id || 0,
          anioLectivo: sectionToChange?.anioLectivo || 0,
        })
      );
    }
  };

  const handleDesktopTab = (tab: "calendario" | "materias") => {
    dispatch(setDesktopTab(tab));
  };

  const handlTryChangeSection = async (idCurso: number, anioLectivo: number) => {
    dispatch(setSectionToChange(null));

    try {
      const changeSectionResult = await dispatch(
        changeSectionThunk({
          //@ts-ignore
          tic: confirmChangeSectionData.reserveData.tic,
          //@ts-ignore
          tim: confirmChangeSectionData.reserveData.tim,
          //@ts-ignore
          idC: confirmChangeSectionData.reserveData.idC,
          //@ts-ignore
          idA: confirmChangeSectionData.reserveData.idA,
          //@ts-ignore
          idS: confirmChangeSectionData.reserveData.idS,
          //@ts-ignore
          aL: confirmChangeSectionData.reserveData.aL,
          //@ts-ignore
          idMateria: confirmChangeSectionData.reserveData.idMateria,
          //@ts-ignore
          idPersona: confirmChangeSectionData.reserveData.idPersona,
        })
      );
      if (isRejected(changeSectionResult)) {
        showError(changeSectionResult.payload as string);
      }
    } catch (error: any) {
      dispatch(removeSubProcessSectionIds({ idCurso, anioLectivo }));
    }

    dispatch(setConfirmChangeSectionData(null));
    clearData(false);
    setWasChangeSectionConfirmed(false);
  };

  const handleConfirmacionSuperposicion = () => {
    setShowModalSuperposicion(false);
    setShowModalChangeSection(true);
  };

  if (!oferta && student?.id) {
    return <TransitionModal open={true} text={"logoAnimado"} />;
  }
  //  ---------------  DESKTOP MAIN  -----------------
  const renderContent = () => {
    if (!turnstileVerified) {
      return (
        <div className="flex justify-center items-center h-full">
          <Turnstile sitekey={process.env.REACT_APP_TURNSTILE_SITEKEY!} onVerify={handleVerify} />
        </div>
      );
    } else {
      return (
        <div className="desktop-main">
          <div className="desktop-left">
            <h2 className="inscripciones-title">
              <Translatable as="span" capitalize>Inscripciones</Translatable>
            </h2>
            <div className="inner-left-box">
              <div className="desktop-inscribed-calendar-tab">
                <button
                  className={`desktop-left-tab pb-2 ${desktopTab === "materias" ? "desktop-left-tab-active" : ""}`}
                  onClick={() => handleDesktopTab("materias")}
                >
                  <Translatable as="span" capitalize>Mis cursos</Translatable>
                  <p className="desktop-inscribed-quantity">{!inscribedSubjects ? 0 : inscribedSubjects.length}</p>
                </button>
                {student.mostrarHorarios && (
                  <button
                    className={`desktop-left-tab pb-2 ${desktopTab === "calendario" ? "desktop-left-tab-active" : ""}`}
                    onClick={() => handleDesktopTab("calendario")}
                  >
                    <Translatable as="span" capitalize>Calendario</Translatable>
                  </button>
                )}
              </div>
              {desktopTab === "materias" && <DesktopSelectedSubjects />}
              {desktopTab === "calendario" && student.mostrarHorarios && <FlexCalendar />}
            </div>
          </div>
          <div className="desktop-right">
            <BannersContainer />
            <OfferVirtualListContainer />
          </div>
          {sectionToChange && showModalChangeSection && (
            <Modal
              open={true}
              onClose={() => clearData(!wasChangeSectionConfirmed)}
              message=""
              messageHtml={
                <p style={{ fontWeight: "400" }}>
                  <Translatable as="span">En caso de que el cambio NO sea posible, tu cupo original en</Translatable>{" "}
                  <strong>{inscribedTitle}</strong>{" "}
                  <Translatable as="span">
                    será reservado. De esta forma, no perderás tu cupo para cursar la materia.
                  </Translatable>
                </p>
              }
              topBorderWarn={false}
              closeButton={{
                label: cancelLabel,
                buttonFn: clearData,
                color: "#00C4B3",
              }}
              okButton={{
                label: continueLabel,
                buttonFn: async () => {
                  setWasChangeSectionConfirmed(true);
                  await handlTryChangeSection(sectionToChange.id, sectionToChange.anioLectivo);
                },
                color: "#00C4B3",
              }}
            />
          )}
          {sectionToChange && showModalSuperposicion && (
            <Modal
              open={true}
              onClose={clearData}
              message=""
              messageHtml={
                <p style={{ fontWeight: "400" }}>
                  <Translatable as="span">Si cambias de sección a</Translatable>{" "}
                  <strong>{offerTitle}</strong>{" "}
                  <Translatable as="span">el horario se superpondrá con</Translatable>{" "}
                  <strong>{overlapTitle}</strong>
                  <Translatable as="span">. ¿Deseas confirmar igualmente?</Translatable>
                </p>
              }
              topBorderWarn={true}
              closeButton={{
                label: cancelLabel,
                buttonFn: () => {
                  clearData();
                },
                color: "#f5a800",
              }}
              okButton={{
                label: confirmLabel,
                buttonFn: handleConfirmacionSuperposicion,
                color: "#f5a800",
              }}
            />
          )}
        </div>
      );
    }
  };

  return (
    <InvalidatingOfferLoader>{renderContent()}</InvalidatingOfferLoader>
  );
}

export default DesktopMain;
